import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDoc, doc, collection, getDocs, query, where } from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import { db, auth } from "../config/firebase";

const DeviceLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleDeviceLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // First, authenticate with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Now find the device record using the email
      const devicesRef = collection(db, "devices");
      const deviceQuery = query(devicesRef, where("email", "==", email));
      const deviceSnapshot = await getDocs(deviceQuery);

      if (deviceSnapshot.empty) {
        setError("Device not found with this email");
        setLoading(false);
        return;
      }

      const deviceDoc = deviceSnapshot.docs[0];
      const deviceData = deviceDoc.data();

      // Store the Firebase UID in the device record for future use (optional)
      console.log("Device found:", deviceDoc.id);
      console.log("Firebase UID:", user.uid);
      console.log("Device data:", deviceData);

      // Check auth_type (if field exists)
      if (deviceData.auth_type && deviceData.auth_type !== "email_password") {
        setError("Invalid authentication method for this device");
        setLoading(false);
        return;
      }

      // Check if device is active
      if (!deviceData.active) {
        setError("Device is not active");
        setLoading(false);
        return;
      }

      // Get school information
      const schoolRef = doc(db, "schools", deviceData.school_id);
      const schoolSnap = await getDoc(schoolRef);

      if (!schoolSnap.exists()) {
        setError("School not found");
        setLoading(false);
        return;
      }

      const schoolData = schoolSnap.data();

      // Device login successful, proceed to teacher login
      navigate("/teacher-login", { 
        state: { 
          schoolId: deviceData.school_id,
          deviceId: deviceDoc.id,
          schoolName: schoolData.name,
          deviceName: deviceData.device_name || "Unknown Device",
          firebaseUid: user.uid
        } 
      });

    } catch (error) {
      console.error("Device login error:", error);
      
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/user-not-found') {
        setError("No account found with this email");
      } else if (error.code === 'auth/wrong-password') {
        setError("Invalid password");
      } else if (error.code === 'auth/invalid-email') {
        setError("Invalid email format");
      } else if (error.code === 'auth/user-disabled') {
        setError("This account has been disabled");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleDeviceLogin();
    }
  };

  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  return (
    <div 
      className="device-login-container flex flex-col items-center justify-center h-screen"
      style={{ backgroundColor: safeLineColors.lightGray }}
    >
      <div 
        className="p-8 rounded-xl shadow-2xl w-96 relative overflow-hidden"
        style={{ 
          backgroundColor: safeLineColors.pureWhite,
          border: `1px solid ${safeLineColors.coolGray}`,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Orange accent bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-1"
          style={{ 
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }}
        />
        
        {/* Header */}
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: safeLineColors.orangeTint }}
          >
            <svg className="w-8 h-8" style={{ color: safeLineColors.flameOrange }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
            </svg>
          </div>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: safeLineColors.darkCharcoal }}
          >
            SafeLine Device Login
          </h1>
          <p 
            className="text-base"
            style={{ color: safeLineColors.mediumCharcoal }}
          >
            Secure access to pickup management
          </p>
        </div>
        
        {error && (
          <div 
            className="px-4 py-3 rounded-lg mb-6 border"
            style={{ 
              backgroundColor: safeLineColors.lightRed,
              borderColor: safeLineColors.red,
              color: safeLineColors.darkRed
            }}
          >
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label 
              className="block text-sm font-semibold mb-3"
              style={{ color: safeLineColors.darkCharcoal }}
            >
              Device Email
            </label>
            <div className="relative">
              <div 
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: safeLineColors.flameOrange }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-12 pr-4 py-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: safeLineColors.pureWhite,
                  border: `2px solid ${safeLineColors.coolGray}`,
                  color: safeLineColors.darkCharcoal,
                  fontSize: '16px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = safeLineColors.flameOrange;
                  e.target.style.boxShadow = `0 0 0 3px ${safeLineColors.orangeTint}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = safeLineColors.coolGray;
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter device email"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label 
              className="block text-sm font-semibold mb-3"
              style={{ color: safeLineColors.darkCharcoal }}
            >
              Password
            </label>
            <div className="relative">
              <div 
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: safeLineColors.flameOrange }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-12 pr-4 py-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: safeLineColors.pureWhite,
                  border: `2px solid ${safeLineColors.coolGray}`,
                  color: safeLineColors.darkCharcoal,
                  fontSize: '16px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = safeLineColors.flameOrange;
                  e.target.style.boxShadow = `0 0 0 3px ${safeLineColors.orangeTint}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = safeLineColors.coolGray;
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter device password"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleDeviceLogin}
          disabled={loading}
          className="w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 mt-8 relative overflow-hidden"
          style={{
            background: loading 
              ? safeLineColors.mutedGray 
              : `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
            color: safeLineColors.pureWhite,
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading 
              ? 'none' 
              : `0 4px 15px rgba(255, 107, 53, 0.3)`,
            transform: loading ? 'none' : 'translateY(0)'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.background = `linear-gradient(135deg, ${safeLineColors.deepOrange}, ${safeLineColors.flameOrange})`;
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 6px 20px rgba(255, 107, 53, 0.4)`;
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.background = `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 4px 15px rgba(255, 107, 53, 0.3)`;
            }
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div 
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"
              />
              Authenticating...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Login to Device
            </div>
          )}
        </button>

        <div className="mt-6 text-center">
          <p 
            className="text-sm"
            style={{ color: safeLineColors.lightCharcoal }}
          >
            Enter your device credentials to access SafeLine
          </p>
          <div 
            className="mt-3 text-xs"
            style={{ color: safeLineColors.mutedGray }}
          >
            &copy; {new Date().getFullYear()} SafeLine. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceLogin;